'use client';

import {
  Children,
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

/** Matches MOBILE_MAX in appearance-css.ts, where columns stop sitting side by side. */
const MOBILE_QUERY = '(max-width: 768px)';

interface CollapsibleColumnProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  dataAttrs?: Record<string, string>;
  /** Label for the toggle, for assistive tech that reads the button alone. */
  toggleLabel: string;
}

/**
 * A PageBuilder column whose body collapses on phones — "pb-collapse-mobile".
 *
 * Three columns of driving directions read well side by side and badly stacked:
 * on a phone the reader scrolls past two sets of instructions to reach the one
 * they want. Above 768px this renders the column untouched; below it, the
 * column's FIRST element becomes a toggle and everything after it hides until
 * tapped.
 *
 * The split is by position rather than by class so the markup an author has to
 * produce stays ordinary: a heading, then content. In practice the heading
 * carries `pb-panel-title`, but nothing here depends on that.
 *
 * WHY JAVASCRIPT rather than a CSS media query: the collapsed state has to be
 * real. A CSS-only version keeps the button in the DOM at every width, so a
 * screen reader on a desktop announces "collapsed" over text that is plainly
 * visible. Switching on `matchMedia` means the button only exists where it
 * does something.
 *
 * OPEN ON THE SERVER, closed on mount for narrow viewports. That ordering is
 * deliberate: desktop — the common case — never flashes, and if hydration never
 * happens the page shows every direction rather than hiding them behind
 * controls that cannot respond. Hidden content behind a dead button is the one
 * failure this page cannot afford.
 */
export function CollapsibleColumn({
  children,
  className,
  style,
  dataAttrs,
  toggleLabel,
}: CollapsibleColumnProps) {
  const items = Children.toArray(children);
  const header = items[0] ?? null;
  const body = items.slice(1);
  const panelId = useId();

  const [collapsible, setCollapsible] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const apply = () => {
      setCollapsible(mq.matches);
      // Crossing the breakpoint resets the disclosure: coming back to a phone
      // width should start closed again, which is the whole point of it.
      setOpen(!mq.matches);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Nothing to collapse — a column with only a heading would otherwise render a
  // toggle that reveals nothing.
  if (!collapsible || body.length === 0) {
    return (
      <div className={className} style={style} {...dataAttrs} data-content-type="column">
        {children}
      </div>
    );
  }

  return (
    // `data-collapsible` marks the state the presentation belongs to, so the
    // hairline and the padding land only while the toggle is really there.
    <div
      className={className}
      style={style}
      {...dataAttrs}
      data-content-type="column"
      data-collapsible=""
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={toggleLabel}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        // Presentation lives in globals.css against these two hooks, shared
        // with the native <details> the pb-collapse rows render — the two
        // disclosures should be indistinguishable, and they stopped being so
        // the moment one of them carried its own utilities.
        className="pb-collapse-toggle"
      >
        {header}
        {/* Always a plus, turned into a cross by CSS. Swapping the glyph for a
            minus made the mark blink; rotating it moves. */}
        <span aria-hidden="true" className="pb-collapse-marker" data-open={open ? '' : undefined} />
      </button>
      <div id={panelId} className="pb-collapse-body" hidden={!open}>
        {body}
      </div>
    </div>
  );
}
